/**
 * Thin GitHub REST client — PUBLIC repos only, read-mostly.
 *
 * Deliberately tiny: the demo only needs a handful of read endpoints plus
 * one DRY-RUN write. No OAuth, no user accounts, no mutations. An optional
 * token (server-side env) only raises the anonymous rate limit; it is never
 * a user's token.
 */

const GH_API = "https://api.github.com";

export interface GitHubClientOptions {
  token?: string;
  fetch?: typeof fetch;
}

export interface PullSummary {
  number: number;
  title: string;
  user: string;
  createdAt: string;
  draft: boolean;
}

export interface PullDetail extends PullSummary {
  body: string;
  additions: number;
  deletions: number;
  changedFiles: number;
  baseRef: string;
  headRef: string;
}

export interface PullFile {
  filename: string;
  status: string;
  additions: number;
  deletions: number;
}

export interface IssueSummary {
  number: number;
  title: string;
  labels: string[];
  comments: number;
  createdAt: string;
}

export class GitHubError extends Error {
  readonly status: number;
  constructor(message: string, status: number) {
    super(message);
    this.name = "GitHubError";
    this.status = status;
  }
}

export class GitHubClient {
  private readonly token?: string;
  private readonly fetchImpl: typeof fetch;

  constructor(opts: GitHubClientOptions = {}) {
    if (opts.token) this.token = opts.token;
    // Bind to the global. Browsers throw "Illegal invocation" if window.fetch
    // is called as a method on another object (i.e. this.fetchImpl(...) with
    // this === GitHubClient). Node's fetch is receiver-agnostic, which is why
    // this only bit in a real browser.
    this.fetchImpl = opts.fetch ?? globalThis.fetch.bind(globalThis);
  }

  async getRepo(owner: string, repo: string): Promise<{
    fullName: string;
    description: string | null;
    stars: number;
    openIssues: number;
    defaultBranch: string;
  }> {
    const r = await this.get<Record<string, unknown>>(`/repos/${owner}/${repo}`);
    return {
      fullName: String(r.full_name ?? `${owner}/${repo}`),
      description: (r.description as string | null) ?? null,
      stars: Number(r.stargazers_count ?? 0),
      openIssues: Number(r.open_issues_count ?? 0),
      defaultBranch: String(r.default_branch ?? "main"),
    };
  }

  async listPulls(owner: string, repo: string, state = "open"): Promise<PullSummary[]> {
    // GitHub returns most-recently-created first for state=open by default
    // (sort=created, direction=desc), so element [0] is the newest.
    const arr = await this.get<Array<Record<string, unknown>>>(
      `/repos/${owner}/${repo}/pulls?state=${encodeURIComponent(state)}&per_page=20&sort=created&direction=desc`,
    );
    return arr.map((p) => ({
      number: Number(p.number),
      title: String(p.title ?? ""),
      user: String((p.user as Record<string, unknown> | null)?.login ?? "unknown"),
      createdAt: String(p.created_at ?? ""),
      draft: Boolean(p.draft),
    }));
  }

  async getPull(owner: string, repo: string, number: number): Promise<PullDetail> {
    const p = await this.get<Record<string, unknown>>(`/repos/${owner}/${repo}/pulls/${number}`);
    return {
      number: Number(p.number),
      title: String(p.title ?? ""),
      body: String(p.body ?? ""),
      user: String((p.user as Record<string, unknown> | null)?.login ?? "unknown"),
      createdAt: String(p.created_at ?? ""),
      draft: Boolean(p.draft),
      additions: Number(p.additions ?? 0),
      deletions: Number(p.deletions ?? 0),
      changedFiles: Number(p.changed_files ?? 0),
      baseRef: String((p.base as Record<string, unknown> | null)?.ref ?? ""),
      headRef: String((p.head as Record<string, unknown> | null)?.ref ?? ""),
    };
  }

  async listPullFiles(owner: string, repo: string, number: number): Promise<PullFile[]> {
    const arr = await this.get<Array<Record<string, unknown>>>(
      `/repos/${owner}/${repo}/pulls/${number}/files?per_page=50`,
    );
    return arr.map((f) => ({
      filename: String(f.filename ?? ""),
      status: String(f.status ?? ""),
      additions: Number(f.additions ?? 0),
      deletions: Number(f.deletions ?? 0),
    }));
  }

  async listIssues(
    owner: string,
    repo: string,
    state = "open",
    labels?: string,
  ): Promise<IssueSummary[]> {
    const labelQ = labels ? `&labels=${encodeURIComponent(labels)}` : "";
    const arr = await this.get<Array<Record<string, unknown>>>(
      `/repos/${owner}/${repo}/issues?state=${encodeURIComponent(state)}&per_page=20${labelQ}`,
    );
    // The issues endpoint also returns PRs; filter those out.
    return arr
      .filter((i) => i.pull_request === undefined)
      .map((i) => ({
        number: Number(i.number),
        title: String(i.title ?? ""),
        labels: ((i.labels as Array<Record<string, unknown>>) ?? []).map((l) =>
          String(l.name ?? ""),
        ),
        comments: Number(i.comments ?? 0),
        createdAt: String(i.created_at ?? ""),
      }));
  }

  private async get<T>(path: string): Promise<T> {
    let res: Response;
    try {
      res = await this.fetchImpl(`${GH_API}${path}`, { headers: this.headers() });
    } catch (cause) {
      throw new GitHubError(
        `network error calling GitHub: ${cause instanceof Error ? cause.message : String(cause)}`,
        0,
      );
    }
    if (!res.ok) {
      const body = await res.text().catch(() => "");
      throw new GitHubError(
        `GitHub ${res.status} on ${path}${body ? `: ${body.slice(0, 200)}` : ""}`,
        res.status,
      );
    }
    return (await res.json()) as T;
  }

  private headers(): Record<string, string> {
    const h: Record<string, string> = {
      Accept: "application/vnd.github+json",
      "User-Agent": "macrokit-studio-preview",
      "X-GitHub-Api-Version": "2022-11-28",
    };
    if (this.token) h.Authorization = `Bearer ${this.token}`;
    return h;
  }
}
